import Container from './container/container';
import InteractiveContainerEvent from './interactive-container-event';

/** The base interactive component you extend from, when creating pointer and focus enabled UI components. */
export default class InteractiveContainer extends Container {
	/**
	 * The enabled property sets if the component is interactive or not.
	 *
	 * Setting enabled to false, will disable all pointer- and focus-events and set the state to "disabled"
	 *
	 * @param {boolean} value True if the component is interactive or false. default is true
	 */
	public set enabled(value: boolean) {
		if (this.#enabled === value) {
			return;
		}
		assertIsBoolean(value);
		this.#enabled = value;
		this.#enabledChanged = true;
		this.invalidateProperties();
	}

	public get enabled(): boolean {
		return this.#enabled;
	}

	/**
	 * The tabbable property sets whether the component can be reached with the Tab key.
	 *
	 * True by default. Set it to false for a component the user is meant to reach some other way — arrow keys into a list, say — and it stays focusable by focus() and by pointer, but the Tab key passes it by.
	 *
	 * Disabling a component takes it out of the tab order whatever this says, since a component that cannot be interacted with has no business being tabbed to.
	 *
	 * @param {boolean} value True if the Tab key should reach the component. default is true
	 */
	public set tabbable(value: boolean) {
		if (this.#tabbable === value) {
			return;
		}
		assertIsBoolean(value);
		this.#tabbable = value;
		this.#updateTabIndex();
	}

	public get tabbable(): boolean {
		return this.#tabbable;
	}

	/** @returns The current state */
	public get state(): 'disabled' | 'focused' | 'focusedHovered' | 'focusedPressed' | 'hovered' | 'idle' | 'pressed' {
		return this.#state;
	}

	/**
	 * The state as a component styles it, which is the state with the pointer's hover left out.
	 *
	 * Hovering is not styled: the cursor is what tells the user a component can be interacted with, and a hover look is too easily mistaken for a selected one. So "hovered" is styled as "idle" and "focusedHovered" as "focused".
	 *
	 * A subclass that dresses itself from this rather than from state has no hover state it could forget to answer. That matters because the states arrive one after another: a component blurred while the pointer is still over it goes from "focusedHovered" to "hovered", and a branch left out for "hovered" would leave
	 * the focused look standing until the pointer moved away.
	 *
	 * @returns The current state, with "hovered" as "idle" and "focusedHovered" as "focused"
	 */
	protected get styledState(): 'disabled' | 'focused' | 'focusedPressed' | 'idle' | 'pressed' {
		if (this.#state === 'hovered') {
			return 'idle';
		} else if (this.#state === 'focusedHovered') {
			return 'focused';
		}
		return this.#state;
	}

	#enabled: boolean;
	#enabledChanged: boolean;
	#focused: boolean;
	// we keep track of when we add or remove KeyboardEvent listener so we don't get memory leaks.
	#hasKeyboardListener: boolean;
	#hovered: boolean;
	#pressed: boolean;
	#tabbable: boolean;
	#state: 'disabled' | 'focused' | 'focusedHovered' | 'focusedPressed' | 'hovered' | 'idle' | 'pressed';

	public constructor() {
		super();
		this.#enabled = true;
		this.#enabledChanged = false;
		this.#hasKeyboardListener = false;
		this.#focused = false;
		this.#hovered = false;
		this.#pressed = false;
		this.#state = 'idle';
		this.#tabbable = true;
	}

	/**
	 * The clicked method will be invoked every time the user clicks the InteractiveContainer
	 *
	 * Or releases the Space or Enter key while focused.
	 */
	protected clicked(_: Event): void {
		// override in sub classes
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#enabledChanged) {
			this.#commitEnabled();
		}
	}

	/**
	 * Forgets the pointer and the focus on the way out of the DOM.
	 *
	 * No boundary event is fired for an element that is taken out of the document: the pointer never moved, so from the browser's side nothing was left. A component removed while the pointer was over it would go on saying it is hovered, and go on wearing whatever its state dresses it in.
	 *
	 * That matters because a removed component is not always a finished one. SelectList hands its ItemRenderers to a pool and gives them back out the next time the data changes, and one that went in hovered comes back out hovered — painted for a pointer that is somewhere else entirely, on a row that may not even hold
	 * the same Item any more.
	 *
	 * The focus is forgotten too. Chromium blurs a focused element that is removed, and says so, so there the component has left the focused states before it gets here — but not every browser does. One that heard no blur would come back out of the pool dressed as focused, and would go on listening on the window for
	 * the Space and Enter it answers while focused, so a key pressed anywhere else would click a component nobody can see. Leaving the focused states is what takes that listener off again. A move with moveBefore() keeps the focus and never gets here: Component answers it with connectedMoveCallback() instead.
	 */
	protected override disconnected(): void {
		super.disconnected();
		this.#focused = false;
		this.#hovered = false;
		this.#pressed = false;
		// a disabled component is held at "disabled" by the enabled property rather than by these flags, and #getStateFromFlags does not know that state, so asking it here would quietly enable the component again
		if (this.#enabled) {
			this.#invokeIfStateChanged(this.#getStateFromFlags());
		}
	}

	/**
	 * The enabledChanged method will be invoked every time the enabled property changes.
	 *
	 * Override the method in sub classes to specify how the Component will behave or be styled.
	 */
	protected enabledChanged(): void {
		// override in sub classes
	}

	protected override initialize(): void {
		super.initialize();
		this.#updateTabIndex();
		// we bind the keyboardListener, since we are listening in the window scope.
		// we can't bind #private methods, so we do it typesafe instead.
		this.keyboardListener = this.keyboardListener.bind(this);
		this.#addEventListeners();
	}

	/**
	 * The stateChanged method will be invoked every time the state changes.
	 *
	 * Override the method in sub classes to specify how the Component will behave or be styled.
	 */
	protected stateChanged(): void {
		// override in sub classes
	}

	#addEventListeners(): void {
		this.addEventListener('pointerenter', this.#eventsListener);
		this.addEventListener('pointerdown', this.#eventsListener);
		this.addEventListener('pointerup', this.#eventsListener);
		this.addEventListener('pointerleave', this.#eventsListener);
		this.addEventListener('pointercancel', this.#eventsListener);
		this.addEventListener('focus', this.#eventsListener);
		this.addEventListener('blur', this.#eventsListener);
		this.addEventListener('click', this.#clickListener);
	}

	#addKeyboardListener(): void {
		if (!this.#hasKeyboardListener) {
			window.addEventListener('keydown', this.keyboardListener);
			window.addEventListener('keyup', this.keyboardListener);
			this.#hasKeyboardListener = true;
		}
	}

	#addOrRemoveKeyboardListener(): void {
		if (this.state.startsWith('focused')) {
			this.#addKeyboardListener();
		} else {
			this.#removeKeyboardListener();
		}
	}

	/**
	 * If we need to override how the click handler works, stop Event from propagating, toggle flags, dispatch events, etc.
	 *
	 * We can override the clickListener in sub classes.
	 */
	#clickListener(event: Event): void {
		this.clicked(event);
	}

	#commitEnabled(): void {
		this.#enabledChanged = false;
		if (this.#enabled) {
			this.#updateTabIndex();
			this.#invokeIfStateChanged('idle');
			this.#addEventListeners();
		} else {
			// we call blur() here,
			// so the browser can handle correct focus management
			// when toggling enabled = true/false
			this.blur();
			this.#updateTabIndex();
			this.#invokeIfStateChanged('disabled');
			this.#removeEventListeners();
		}
		this.enabledChanged();
	}

	#dispatchFocusEvents(event: FocusEvent): void {
		if (event.type === 'focus') {
			this.dispatchEvent(new InteractiveContainerEvent(InteractiveContainerEvent.FOCUSED));
		} else if (event.type === 'blur') {
			this.dispatchEvent(new InteractiveContainerEvent(InteractiveContainerEvent.BLURRED));
		}
	}

	#dispatchInteractiveContainerEvents(event: FocusEvent | KeyboardEvent | PointerEvent): void {
		if (event instanceof FocusEvent) {
			this.#dispatchFocusEvents(event);
		} else if (event instanceof PointerEvent) {
			this.#dispatchPointerEvents(event);
		}
	}

	#dispatchPointerEvents(event: PointerEvent): void {
		if (event.type === 'pointerenter') {
			this.dispatchEvent(new InteractiveContainerEvent(InteractiveContainerEvent.HOVERED));
		} else if (event.type === 'pointerleave') {
			this.dispatchEvent(new InteractiveContainerEvent(InteractiveContainerEvent.UN_HOVERED));
		}
	}

	#eventsListener(event: FocusEvent | KeyboardEvent | PointerEvent): void {
		switch (event.type) {
			case 'blur':
				this.#focused = false;
				break;
			case 'focus':
				this.#focused = true;
				break;
			case 'keydown':
				this.#pressed = true;
				break;
			case 'keyup':
				this.#pressed = false;
				break;
			case 'pointerdown':
				this.#pressed = true;
				break;
			case 'pointerenter':
				this.#hovered = true;
				break;
			// a cancelled pointer is a pointer that is gone, not merely one that has been let go of, so it
			// clears both flags exactly as leaving does. It dispatches nothing of its own: the spec has a
			// pointerout and a pointerleave follow every pointercancel, and that pointerleave is what says
			// UN_HOVERED — by which time these flags have already settled, so it says it once
			case 'pointercancel':
			case 'pointerleave':
				this.#hovered = false;
				this.#pressed = false;
				break;
			case 'pointerup':
				this.#pressed = false;
				break;
		}
		const state: 'disabled' | 'focused' | 'focusedHovered' | 'focusedPressed' | 'hovered' | 'idle' | 'pressed' = this.#getStateFromFlags();
		this.#invokeIfStateChanged(state);
		this.#dispatchInteractiveContainerEvents(event);
	}

	#getFocusedStateFromFlags(): 'focused' | 'focusedHovered' | 'focusedPressed' {
		// order is important, pressed has to be first since hovered is fired first
		if (this.#pressed) {
			return 'focusedPressed';
		} else if (this.#hovered) {
			return 'focusedHovered';
		}
		return 'focused';
	}

	#getNotFocusedStateFromFlags(): 'hovered' | 'idle' | 'pressed' {
		// order is important, pressed has to be first since hovered is fired first
		if (this.#pressed) {
			return 'pressed';
		} else if (this.#hovered) {
			return 'hovered';
		}
		return 'idle';
	}

	#getStateFromFlags(): 'disabled' | 'focused' | 'focusedHovered' | 'focusedPressed' | 'hovered' | 'idle' | 'pressed' {
		if (this.#focused) {
			return this.#getFocusedStateFromFlags();
		}
		return this.#getNotFocusedStateFromFlags();
	}

	/** A tabIndex of -1 is out of the tab order but still focusable by focus() and by pointer, which is what both a disabled and an untabbable component wants. */
	#updateTabIndex(): void {
		this.tabIndex = this.#enabled && this.#tabbable ? 0 : -1;
	}

	#invokeIfStateChanged(value: 'disabled' | 'focused' | 'focusedHovered' | 'focusedPressed' | 'hovered' | 'idle' | 'pressed'): void {
		if (this.#state !== value) {
			this.#state = value;
			this.#addOrRemoveKeyboardListener();
			this.stateChanged();
		}
	}

	#removeEventListeners(): void {
		this.removeEventListener('pointerenter', this.#eventsListener);
		this.removeEventListener('pointerdown', this.#eventsListener);
		this.removeEventListener('pointerup', this.#eventsListener);
		this.removeEventListener('pointerleave', this.#eventsListener);
		this.removeEventListener('pointercancel', this.#eventsListener);
		this.removeEventListener('focus', this.#eventsListener);
		this.removeEventListener('blur', this.#eventsListener);
		this.removeEventListener('click', this.#clickListener);
	}

	#removeKeyboardListener(): void {
		if (this.#hasKeyboardListener) {
			window.removeEventListener('keydown', this.keyboardListener);
			window.removeEventListener('keyup', this.keyboardListener);
			this.#hasKeyboardListener = false;
		}
	}

	private keyboardListener(event: KeyboardEvent): void {
		if (event.code === 'Space' || event.code === 'Enter' || event.code === 'NumpadEnter') {
			// if we press Space inside a cointainer that overflows it's content,
			// the scroll position scrolls down to the bottom, which we don't want
			// so the event.preventDefault() call prevents this behavior
			if (event.code === 'Space') {
				event.preventDefault();
			}
			this.#eventsListener(event);
			if (event.type === 'keyup') {
				this.clicked(event);
			}
		}
	}
}
customElements.define('ac-interactive-container', InteractiveContainer);

function assertIsBoolean(value: unknown): asserts value is boolean {
	if (typeof value === 'boolean') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type boolean`);
}
